/* eslint-disable @typescript-eslint/no-explicit-any
  --
  Using any is fine here because we are only using it to cast the result of the safeParse
*/
import type { infer as InferSchema } from "zod";
import { z } from "zod";
import { zfd } from "zod-form-data";

const passwordRequirements = [
  {
    name: "At least 8 characters",
    validation: (value?: string) => value && value.length >= 8,
  },

  {
    name: "At least 1 upper-case letter",
    validation: (value?: string) => value && /^$|^(?=.*[A-Z]).*$/.test(value),
  },

  {
    name: "At least 1 lower-case letter",
    validation: (value?: string) => value && /^$|^(?=.*[a-z]).*$/.test(value),
  },

  {
    name: "At least 1 number",
    validation: (value?: string) => value && /^$|^(?=.*\d).*$/.test(value),
  },

  {
    name: 'At least 1 special character (!@#$%^&*(),.?":{}|<>)',
    validation: (value?: string) =>
      value && /^$|^(?=.*[!@#$%^&*(),.?":{}|<>]).*$/.test(value),
  },

  {
    name: "Not Contain more than 2 identical consecutive characters",
    validation: (value?: string) => value && !/^(.)\1\1.*$/.test(value),
  },
] as const;

const isSecurePassword = (value: string): boolean => {
  return !passwordRequirements.some(
    (requirement) => !requirement.validation(value),
  );
};

type ZodInjection = {
  z: typeof z;
  zfd: typeof zfd;
};

type InferSchemaError<TSchema extends z.ZodType<any, any, any>> = Record<
  keyof InferSchema<TSchema>,
  string
>;

/**
 * https://stackoverflow.com/questions/69329657/how-to-transform-a-recursive-generics-shape-typescript
 *
 * @example
 * createSchema(({ z, zfd }) => {
 *   return z.object({
 *     name: zfd.text()
 *   })
 * })
 */
const createSchema = <Input extends Record<string, unknown>, Output>(
  factoryFn: (zodInjection: ZodInjection) => z.ZodType<Output, any, Input>,
): z.ZodType<Output, any, Input> => factoryFn({ z, zfd });

const getFormData = async <Input extends Record<string, unknown>, Output>(
  requestOrFormData: Request | FormData,
  schema: z.ZodType<Output, any, Input>,
): Promise<
  | z.SafeParseSuccess<Output>
  | {
      success: false;
      errors: Record<keyof Output, string>;
    }
> => {
  const values = Object.fromEntries(
    requestOrFormData instanceof FormData
      ? requestOrFormData
      : await requestOrFormData.formData(),
  );

  const result = schema.safeParse(values);

  if (result.success) {
    return result;
  }

  return {
    errors: result.error.issues.reduce(
      (acc, issue) => ({ ...acc, [issue.path[0]]: issue.message }),
      {} as Record<keyof InferSchema<typeof schema>, string>,
    ),
    success: false,
  };
};

const getSearchParams = <Input extends Record<string, unknown>, Output>(
  request: Request,
  schema: z.ZodType<Output, any, Input>,
): z.SafeParseReturnType<Input, Output> => {
  const url = new URL(request.url);
  const values = Object.fromEntries(url.searchParams);

  return schema.safeParse(values);
};

export {
  createSchema,
  getFormData,
  getSearchParams,
  isSecurePassword,
  passwordRequirements,
};
export type { InferSchema, InferSchemaError };
